import { createSlice } from '@reduxjs/toolkit';
import axios from 'utils/axios';
import { getFilterDate } from 'utils/formatDateRange';

const initialState = {
  activeStep: 0,
  list: [],
  languages: [],
  onboardingHints: null,
  isLoading: false,
  error: false,
  currentMailing: null,
};

const slice = createSlice({
  name: 'mailing',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getMonetizationMetaStart(state) {
      state.isLoading = true;
    },

    getMonetizationMetaSuccess(state, action) {
      const { languages, onboardingHints } = action.payload;
      state.isLoading = false;
      state.languages = languages ?? [];
      state.onboardingHints = onboardingHints ?? null;
    },

    getMonetizationMetaError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getMailingsStart(state) {
      state.isLoading = true;
    },

    getMailingsSuccess(state, action) {
      state.isLoading = false;
      state.list = action.payload.mailings;
    },

    getMailingsError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    createMailingStart(state) {
      state.isLoading = true;
    },

    createMailingSuccess(state, action) {
      state.isLoading = false;
      state.list.push(action.payload);
    },

    createMailingError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    addToListStart(state) {
      state.isLoading = true;
    },

    addToListSuccess(state, action) {
      state.isLoading = false;
      state.list.push(action.payload);
    },

    addToListError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    onBackStep(state) {
      state.activeStep -= 1;
    },

    onNextStep(state) {
      state.activeStep += 1;
    },

    onGotoStep(state, action) {
      const goToStep = action.payload;
      state.activeStep = goToStep;
    },

    getMailingSuccess(state, action) {
      state.isLoading = false;
      state.currentMailing = action.payload;
    },

    updateMailingSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },

    resetStepperState(state) {
      state.activeStep = 0;
    },
  },
});

export default slice.reducer;

export const {
  onGotoStep,
  onBackStep,
  onNextStep,
  createMailingStart,
  createMailingSuccess,
  createMailingError,
  getMailingsStart,
  getMailingsSuccess,
  getMailingsError,
  getMonetizationMetaStart,
  getMonetizationMetaSuccess,
  getMonetizationMetaError,
  addToListStart,
  addToListSuccess,
  addToListError,
  resetStepperState,
} = slice.actions;

export function getMonetizationMeta(appId) {
  return async (dispatch) => {
    dispatch(getMonetizationMetaStart());
    try {
      const response = await axios.get(`/monetization/${appId}/meta`);
      dispatch(getMonetizationMetaSuccess(response));
    } catch (error) {
      dispatch(getMonetizationMetaError(error));
    }
  };
}

export function getMailings(appId) {
  return async (dispatch) => {
    dispatch(getMailingsStart());
    try {
      const response = await axios.get(`/monetization/${appId}/mailings`);
      dispatch(getMailingsSuccess(response));
    } catch (error) {
      dispatch(getMailingsError(error));
    }
  };
}

export function createMailing(appId, data) {
  return async (dispatch) => {
    dispatch(createMailingStart());

    try {
      const response = await axios.post(`/monetization/${appId}/mailings`, data);
      dispatch(createMailingSuccess(response));
    } catch (error) {
      dispatch(createMailingError(error.response?.data?.message || error.message));
    }
  };
}

export function addToList(appId, listId, page, pageSize, search = []) {
  const updatedSearch = search.map((filter) => {
    if (filter.column === 'creationDate' || filter.column === 'lastSignIn') {
      const { start, end } = getFilterDate(parseInt(filter.additionalValue, 10), filter.value);

      if (filter.operator === '=' || filter.operator === '!=') {
        const newOperator = filter.operator === '=' ? 'between' : 'notBetween';
        return {
          ...filter,
          operator: newOperator,
          value: [start, end],
        };
      }

      return {
        ...filter,
        value: start,
      };
    }

    return { ...filter };
  });

  return async (dispatch) => {
    dispatch(addToListStart());

    try {
      const response = await axios.post(
        `/monetization/${appId}/user-lists/${listId}/add-users?order=creationDate&ordering=desc&page=${page}&limit=${pageSize}`,
        { search: updatedSearch },
      );
      dispatch(addToListSuccess(response));
    } catch (error) {
      dispatch(addToListError(error.response?.data?.message || error.message));
    }
  };
}

export function getMailing(appId, mailingId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/monetization/${appId}/mailings/${mailingId}`);
      dispatch(slice.actions.getMailingSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateMailing(appId, mailingId, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.put(`/monetization/${appId}/mailings/${mailingId}`, data);
      dispatch(slice.actions.updateMailingSuccess());
      dispatch(getMailings(appId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
